/**
 * O acompanhamento das automações: o que elas fizeram, quando, e se deu certo.
 *
 * A lista vem do serviço (`GET /v1/automacoes/execucoes`), da mais nova para a
 * mais antiga, agrupada por dia; "Ao vivo" pergunta de novo a cada 15 s. O
 * detalhe mostra o passo a passo pelos nomes dos blocos. O que está parado
 * numa espera aparece à parte, e quem edita automações pode cancelar.
 *
 * A execução não traz contato: o desenho tinha a coluna, e o serviço não a dá
 * (nem deve, para não levar dado pessoal para fora do CRM).
 *
 * O desenho segue o protótipo v2: os quatro números no alto, a linha inteira
 * clicável com o ícone do gatilho, o chip da versão e o selo com ícone, a
 * linha que acabou de chegar acesa por um instante, e o passo a passo numa
 * linha vertical. O "Em andamento" do protótipo vira "Esperando agora": uma
 * execução do serviço já chega terminada, e o que fica no meio é a espera.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BlocosDoEditor } from '@cliqcrm/contracts';
import { EDITA_AUTOMACOES } from '@/entities/acesso/papel';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useAutomacoes,
  useAutomacoesEmEspera,
  useBlocosDoEditor,
  useExecucoesDasAutomacoes,
  usePainelDasAutomacoes,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { usePapel } from '@/shared/sessao/contexto';
import {
  Botao,
  CabecalhoDeTela,
  CampoSelecao,
  Dialogo,
  FiltroPilula,
  Icone,
  Identificador,
  MenuAcoes,
  Selo,
  type NomeDoIcone,
} from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  FILTROS_DO_RESULTADO,
  RESULTADO,
  agruparPorDia,
  duracao,
  hora,
  oQueAconteceu,
  relativo,
  tituloDoPasso,
  type FiltroDoResultado,
} from '../lib/execucoes';
import './acompanhamento.css';
import './espera.css';

const PAGINA = 50;

type Execucao = NonNullable<ReturnType<typeof useExecucoesDasAutomacoes>['data']>['itens'][number];

/** O ícone do que disparou a execução: um evento, um horário, ou outra automação. */
const ICONE_DO_GATILHO: Record<Execucao['gatilho'], NomeDoIcone> = {
  evento: 'automacao',
  agenda: 'relogio',
  interno: 'repetir',
};

const ICONE_DO_RESULTADO: Record<Execucao['resultado'], NomeDoIcone> = {
  concluida: 'check',
  falhou: 'fechar',
  ignorada: 'pausa',
};

/**
 * As execuções que chegaram desde a última leitura, para acender por um
 * instante. A primeira leitura não acende nada: é a lista, não a novidade.
 */
function useNovas(itens: readonly { id: string }[] | undefined): ReadonlySet<string> {
  const vistas = useRef<Set<string> | null>(null);
  const novas =
    itens && vistas.current
      ? new Set(itens.filter((e) => !vistas.current!.has(e.id)).map((e) => e.id))
      : new Set<string>();
  useEffect(() => {
    if (!itens) return;
    vistas.current ??= new Set();
    for (const e of itens) vistas.current.add(e.id);
  }, [itens]);
  return novas;
}

export function Acompanhamento({
  falhou,
  automacao: automacaoInicial,
  aoAbrirMapa,
}: {
  falhou: boolean;
  automacao: string | null;
  aoAbrirMapa?: (id: string, versao: number | null) => void;
}): ReactNode {
  const papel = usePapel();
  const [aoVivo, definirAoVivo] = useState(true);
  const [resultado, definirResultado] = useState<FiltroDoResultado>(falhou ? 'falhou' : 'todas');
  const [automacao, definirAutomacao] = useState(automacaoInicial ?? '');
  const [limite, definirLimite] = useState(PAGINA);
  const [aberta, definirAberta] = useState<Execucao | null>(null);

  const execucoes = useExecucoesDasAutomacoes(
    {
      limite,
      ...(automacao ? { automacao } : {}),
      ...(resultado !== 'todas' ? { resultado } : {}),
    },
    aoVivo,
  );
  const automacoes = useAutomacoes();
  const arquivadas = useAutomacoes('arquivada');
  const blocos = useBlocosDoEditor();
  const painel = usePainelDasAutomacoes('24h', aoVivo);
  const emEspera = useAutomacoesEmEspera(aoVivo);

  const todas = [...(automacoes.data?.itens ?? []), ...(arquivadas.data?.itens ?? [])];
  const listasLidas = automacoes.isSuccess && arquivadas.isSuccess;
  const nomeDe = (id: string): string => {
    const a = todas.find((x) => x.id === id);
    if (a) return a.nome?.trim() || 'Automação sem nome';
    // Só diz que saiu quando as duas listas chegaram; antes disso, não sabe.
    return listasLidas ? 'Automação removida' : 'Automação';
  };
  const versaoAtual = (id: string): number | undefined => todas.find((x) => x.id === id)?.versao;
  const naoListada = automacao !== '' && listasLidas && !todas.some((x) => x.id === automacao);
  const agora = new Date();
  const novas = useNovas(execucoes.data?.itens);
  const limparFiltros = (): void => {
    definirResultado('todas');
    definirAutomacao('');
    definirLimite(PAGINA);
  };

  return (
    <div className="acompanhamento">
      <CabecalhoDeTela
        titulo="Acompanhamento"
        retrato={
          <span className="acompanhamento__icone-do-titulo" aria-hidden="true">
            <Icone nome="painel" />
          </span>
        }
        apoio="Tudo o que as automações fizeram, na hora em que fizeram. Clique numa linha para ver o passo a passo."
        acoes={
          <button
            type="button"
            className="acompanhamento__ao-vivo"
            aria-pressed={aoVivo}
            onClick={() => definirAoVivo((v) => !v)}
          >
            <span className="acompanhamento__ao-vivo-ponto" aria-hidden="true" />
            {aoVivo ? 'Ao vivo' : 'Pausado'}
          </button>
        }
      />
      <div className="acompanhamento__conteudo">
        <Numeros
          painel={painel}
          emEspera={emEspera}
          aoVerFalhas={() => {
            definirResultado('falhou');
            definirLimite(PAGINA);
          }}
        />

        <EmEspera
          itens={emEspera.data?.itens ?? []}
          nomeDe={nomeDe}
          blocos={blocos.data}
          podeCancelar={papel !== null && EDITA_AUTOMACOES.has(papel)}
          agora={agora}
        />

        <div className="acompanhamento__filtros">
          <div
            className="acompanhamento__resultados"
            role="group"
            aria-label="Filtrar por resultado"
          >
            {FILTROS_DO_RESULTADO.map((f) => (
              <FiltroPilula
                key={f.valor}
                ligado={resultado === f.valor}
                aoAlternar={() => {
                  definirResultado(f.valor);
                  definirLimite(PAGINA);
                }}
              >
                {f.valor !== 'todas' && (
                  <span
                    className="acompanhamento__ponto"
                    data-resultado={f.valor}
                    aria-hidden="true"
                  />
                )}
                {f.rotulo}
              </FiltroPilula>
            ))}
          </div>
          <div className="acompanhamento__automacao">
            <CampoSelecao
              rotuloOculto="Automação"
              tamanho="miudo"
              value={automacao}
              onChange={(e) => {
                definirAutomacao(e.target.value);
                definirLimite(PAGINA);
              }}
            >
              <option value="">Todas as automações</option>
              {todas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome?.trim() || 'Automação sem nome'}
                  {a.situacao === 'arquivada' ? ' (arquivada)' : ''}
                </option>
              ))}
              {naoListada && <option value={automacao}>Esta automação</option>}
            </CampoSelecao>
          </div>
        </div>

        <Estado consulta={execucoes} forma="lista" linhas={6}>
          {(dados) =>
            dados.itens.length === 0 ? (
              resultado === 'todas' && !automacao ? (
                <Vazio
                  titulo="Nada rodou ainda"
                  porque="Quando uma automação ligada fizer alguma coisa, aparece aqui na hora."
                />
              ) : (
                <p className="acompanhamento__nada">
                  Nenhuma execução com esses filtros.{' '}
                  <Botao variante="fantasma" miudo onClick={limparFiltros}>
                    Limpar filtros
                  </Botao>
                </p>
              )
            ) : (
              <>
                <div className="acompanhamento__rolagem">
                  <table className="acompanhamento__tabela">
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Automação e versão</th>
                        <th scope="col" className="acompanhamento__col-oque">
                          O que aconteceu
                        </th>
                        <th scope="col">Resultado</th>
                        <th scope="col" className="acompanhamento__col-seta">
                          <span className="visualmente-oculto">Abrir</span>
                        </th>
                      </tr>
                    </thead>
                    {agruparPorDia(dados.itens, agora).map((grupo) => (
                      <tbody key={grupo.rotulo}>
                        <tr className="acompanhamento__grupo">
                          <th colSpan={5} scope="rowgroup">
                            {grupo.rotulo}
                          </th>
                        </tr>
                        {grupo.itens.map((e) => {
                          const atual = versaoAtual(e.automacao_id);
                          return (
                            <tr
                              key={e.id}
                              className="acompanhamento__linha"
                              data-resultado={e.resultado}
                              data-nova={novas.has(e.id) ? 'sim' : undefined}
                              // A linha inteira abre o detalhe; pelo teclado, o botão do nome.
                              onClick={() => definirAberta(e)}
                            >
                              <td>
                                <span className="acompanhamento__hora">{hora(e.em)}</span>
                                <span className="acompanhamento__relativo">
                                  {relativo(e.em, agora)}
                                </span>
                              </td>
                              <td>
                                <span className="acompanhamento__automacao-celula">
                                  <span
                                    className="acompanhamento__icone-da-linha"
                                    aria-hidden="true"
                                  >
                                    <Icone nome={ICONE_DO_GATILHO[e.gatilho]} />
                                  </span>
                                  <span className="acompanhamento__automacao-textos">
                                    <button
                                      type="button"
                                      className="acompanhamento__abrir"
                                      aria-label={`${nomeDe(e.automacao_id)}, ${hora(e.em)}, ${RESULTADO[e.resultado].rotulo}`}
                                      onClick={() => definirAberta(e)}
                                    >
                                      {nomeDe(e.automacao_id)}
                                    </button>
                                    <VersaoDaLinha
                                      versao={e.versao}
                                      antiga={atual !== undefined && atual !== e.versao}
                                      {...(aoAbrirMapa
                                        ? {
                                            aoAbrir: () =>
                                              aoAbrirMapa(
                                                e.automacao_id,
                                                atual !== undefined && atual !== e.versao
                                                  ? e.versao
                                                  : null,
                                              ),
                                          }
                                        : {})}
                                    />
                                  </span>
                                </span>
                              </td>
                              <td className="acompanhamento__col-oque">
                                {oQueAconteceu(e, blocos.data)}
                              </td>
                              <td>
                                <Selo tom={RESULTADO[e.resultado].tom}>
                                  <Icone nome={ICONE_DO_RESULTADO[e.resultado]} />
                                  {RESULTADO[e.resultado].rotulo}
                                </Selo>
                              </td>
                              <td className="acompanhamento__col-seta" aria-hidden="true">
                                <Icone nome="direita" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    ))}
                  </table>
                </div>
                {dados.proximo_cursor && limite >= 200 && (
                  <p className="acompanhamento__nota">
                    Mostrando as 200 mais recentes. Filtre por resultado ou por automação para ver
                    as anteriores.
                  </p>
                )}
                {dados.proximo_cursor && limite < 200 && (
                  <div className="acompanhamento__mais">
                    <Botao
                      variante="secundario"
                      onClick={() => definirLimite((l) => Math.min(200, l + PAGINA))}
                      desabilitado={execucoes.isFetching}
                      motivo="Carregando…"
                    >
                      Carregar mais
                    </Botao>
                  </div>
                )}
              </>
            )
          }
        </Estado>

        {aberta && (
          <DetalheDaExecucao
            execucao={aberta}
            nome={nomeDe(aberta.automacao_id)}
            versaoAtual={versaoAtual(aberta.automacao_id)}
            blocos={blocos.data}
            aoFechar={() => definirAberta(null)}
            {...(aoAbrirMapa ? { aoAbrirMapa } : {})}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Os quatro números do alto. "Esperando agora" sai da lista das esperas; os
 * outros três, do painel das últimas 24 h. Cada um diz quando não chegou, e a
 * tabela continua.
 */
function Numeros({
  painel,
  emEspera,
  aoVerFalhas,
}: {
  painel: ReturnType<typeof usePainelDasAutomacoes>;
  emEspera: ReturnType<typeof useAutomacoesEmEspera>;
  aoVerFalhas: () => void;
}): ReactNode {
  const ex = painel.data?.execucoes;
  const semPainel = painel.isError && !painel.data;
  const num = (n: number): string => n.toLocaleString('pt-BR');
  const terminadas = ex ? ex.concluidas + ex.falhas : 0;
  const taxa = ex ? (terminadas === 0 ? 100 : Math.round((ex.concluidas / terminadas) * 100)) : 0;
  const apoio = (pronto: string): string =>
    semPainel ? 'não chegou agora' : ex ? pronto : 'carregando';
  const esperando = emEspera.data
    ? `${num(emEspera.data.itens.length)}${emEspera.data.proximo_cursor ? '+' : ''}`
    : '…';
  return (
    <section className="acompanhamento__numeros" aria-label="Números das automações">
      <Numero
        tom="atencao"
        icone="relogio"
        rotulo="Esperando agora"
        valor={emEspera.isError ? '…' : esperando}
        apoio={
          emEspera.isError
            ? 'não chegou agora'
            : emEspera.data
              ? 'um tempo passar ou uma resposta'
              : 'carregando'
        }
      />
      <Numero
        tom="ok"
        icone="check"
        rotulo="Concluídas"
        valor={ex ? num(ex.concluidas) : '…'}
        apoio={apoio('nas últimas 24 h')}
      />
      <Numero
        {...(ex && ex.falhas > 0 ? { tom: 'erro' as const } : {})}
        icone="alerta"
        rotulo="Falharam"
        valor={ex ? num(ex.falhas) : '…'}
        apoio={ex && ex.falhas > 0 ? '' : apoio('nenhuma falha em 24 h')}
        {...(ex && ex.falhas > 0
          ? { acao: { rotulo: 'Ver só as que falharam', aoClicar: aoVerFalhas } }
          : {})}
      />
      <Numero
        tom="roxo"
        icone="automacao"
        rotulo="Taxa de sucesso"
        valor={ex ? `${taxa}%` : '…'}
        apoio={apoio('das que terminaram')}
      />
    </section>
  );
}

function Numero({
  tom,
  icone,
  rotulo,
  valor,
  apoio,
  acao,
}: {
  tom?: 'roxo' | 'ok' | 'erro' | 'atencao';
  icone: NomeDoIcone;
  rotulo: string;
  valor: string;
  apoio: string;
  acao?: { rotulo: string; aoClicar: () => void };
}): ReactNode {
  return (
    <div className="acompanhamento__numero" data-tom={tom}>
      <p className="acompanhamento__numero-topo">
        <span className="acompanhamento__numero-icone" aria-hidden="true">
          <Icone nome={icone} />
        </span>
        {rotulo}
      </p>
      <p className="acompanhamento__numero-valor">{valor}</p>
      {apoio && <p className="acompanhamento__numero-apoio">{apoio}</p>}
      {acao && (
        <button type="button" className="acompanhamento__numero-acao" onClick={acao.aoClicar}>
          {acao.rotulo}
        </button>
      )}
    </div>
  );
}

function DetalheDaExecucao({
  execucao: e,
  nome,
  versaoAtual,
  blocos,
  aoFechar,
  aoAbrirMapa,
}: {
  execucao: Execucao;
  nome: string;
  versaoAtual: number | undefined;
  blocos: BlocosDoEditor | undefined;
  aoFechar: () => void;
  aoAbrirMapa?: (id: string, versao: number | null) => void;
}): ReactNode {
  const antiga = versaoAtual !== undefined && versaoAtual !== e.versao;
  return (
    <Dialogo
      titulo={`${nome}, versão ${e.versao}`}
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Fechar
          </Botao>
          {aoAbrirMapa && (
            <Botao
              variante="primario"
              onClick={() => aoAbrirMapa(e.automacao_id, antiga ? e.versao : null)}
            >
              {antiga ? `Ver a versão ${e.versao}` : 'Ver a automação'}
            </Botao>
          )}
        </>
      }
    >
      <div className="acompanhamento__detalhe">
        <p className="acompanhamento__detalhe-topo">
          <Selo tom={RESULTADO[e.resultado].tom}>
            <Icone nome={ICONE_DO_RESULTADO[e.resultado]} />
            {RESULTADO[e.resultado].rotulo}
          </Selo>
          <span>
            {new Date(e.em).toLocaleString('pt-BR')} · levou {duracao(e.duracao_ms)}
          </span>
        </p>
        {e.resultado === 'falhou' && (
          <Faixa tom="bloqueado">
            <span>
              {oQueAconteceu(e, blocos)}. Para o suporte: código{' '}
              <Identificador tamanho="nota">{e.motivo ?? 'sem código'}</Identificador>, execução{' '}
              <Identificador tamanho="nota">{e.id}</Identificador>.
            </span>
          </Faixa>
        )}
        {antiga && (
          <p className="acompanhamento__nota">
            Rodou na versão {e.versao}. A automação já está na {versaoAtual}.
          </p>
        )}
        <h2 className="acompanhamento__detalhe-titulo">Passo a passo</h2>
        {e.passos.length === 0 ? (
          <p className="acompanhamento__nota">Não passou por nenhum bloco.</p>
        ) : (
          <ol className="acompanhamento__passos">
            {e.passos.map((p, i) => (
              <li
                key={`${p.no ?? p.tipo}-${i}`}
                className="acompanhamento__passo"
                data-resultado={p.resultado}
                style={{ '--i': Math.min(i, 8) } as CSSProperties}
              >
                <span className="acompanhamento__passo-marca" aria-hidden="true">
                  <Icone nome={ICONE_DO_RESULTADO[p.resultado]} />
                </span>
                <span className="acompanhamento__passo-texto">
                  <span className="acompanhamento__passo-nome">
                    {tituloDoPasso(p.tipo, blocos)}
                  </span>
                  <span className="acompanhamento__passo-resultado">
                    {RESULTADO[p.resultado].rotulo}
                    {p.motivo ? `, código ${p.motivo}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Dialogo>
  );
}

type Espera = NonNullable<ReturnType<typeof useAutomacoesEmEspera>['data']>['itens'][number];

/** Quantas linhas o bloco mostra antes do "Ver todas". */
const ESPERAS_A_MOSTRAR = 3;

/**
 * O que a espera aguarda, dito como a pessoa diria. O evento do menu e o da
 * mensagem são os dois que aparecem no dia a dia; os outros vão pelo nome do
 * catálogo, entre aspas: o nome é um fato no passado ("O cliente escolheu…").
 */
function oQueAguarda(espera: Espera, blocos: BlocosDoEditor | undefined): string {
  if (espera.esperando === 'tempo') return 'Aguardando o tempo passar';
  if (espera.evento === 'conversa.resposta_de_botao') {
    return 'Aguardando o cliente escolher uma opção do menu';
  }
  if (espera.evento === 'conversa.mensagem_recebida') return 'Aguardando resposta do cliente';
  const titulo = blocos?.eventos.find((ev) => ev.evento === espera.evento)?.titulo;
  return titulo ? `Aguardando o evento “${titulo}”` : 'Aguardando um evento';
}

/** "Há 2 dias", com maiúscula: começa a linha. */
function haQuanto(desde: string, agora: Date): string {
  const texto = relativo(desde, agora);
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * O que está parado num bloco de espera agora, com o cancelar para quem edita.
 *
 * Um cartão (28/10): o cabeçalho diz quantas e por quê, cada linha diz qual
 * automação, o que ela aguarda e desde quando, e o cancelar mora no menu da
 * linha — seis botões "Cancelar a espera" em fila pareciam a ação principal da
 * tela. Mostra as três mais antigas; "Ver todas" abre o resto.
 */
function EmEspera({
  itens,
  nomeDe,
  blocos,
  podeCancelar,
  agora,
}: {
  itens: readonly Espera[];
  nomeDe: (id: string) => string;
  blocos: BlocosDoEditor | undefined;
  podeCancelar: boolean;
  agora: Date;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [confirmando, definirConfirmando] = useState<string | null>(null);
  const [recado, definirRecado] = useState<string | null>(null);
  const [todas, definirTodas] = useState(false);
  const cancelar = useMutation({
    mutationFn: (id: string) => api!.cancelarEsperaDaAutomacao({ caminho: { id } }),
    onSuccess: (r) =>
      definirRecado(
        r.situacao === 'cancelada'
          ? 'Espera cancelada: a automação parou ali para esta pessoa.'
          : 'A espera já tinha acabado antes do cancelar.',
      ),
    // As execuções e os números mudam junto: a árvore inteira volta a perguntar.
    onSettled: () => {
      definirConfirmando(null);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
    },
  });

  if (itens.length === 0) {
    return recado ? (
      <p className="acompanhamento__nota" role="status">
        {recado}
      </p>
    ) : null;
  }
  const visiveis = todas ? itens : itens.slice(0, ESPERAS_A_MOSTRAR);
  const total = itens.length;
  return (
    <section className="espera" aria-label="Esperando agora">
      <header className="espera__cabeca">
        <span className="espera__icone-grande" aria-hidden="true">
          <Icone nome="relogio" />
        </span>
        <div className="espera__titulos">
          <h2 className="espera__titulo">Automações aguardando</h2>
          <p className="espera__apoio">
            {total === 1
              ? '1 execução pausada, aguardando um evento ou o tempo passar'
              : `${total} execuções pausadas, aguardando um evento ou o tempo passar`}
          </p>
        </div>
        <span className="espera__contagem">
          {total === 1 ? '1 pendente' : `${total} pendentes`}
        </span>
      </header>

      <p className="acompanhamento__nota" role="status">
        {recado ?? ''}
      </p>
      {cancelar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(cancelar.error, {
            generica: 'Não foi possível cancelar agora. Nada mudou; tente de novo.',
          })}
        </Faixa>
      )}

      <ul className="espera__lista">
        {visiveis.map((x) => (
          <li key={x.id} className="espera__linha">
            <span className="espera__icone" aria-hidden="true">
              <Icone nome={x.esperando === 'tempo' ? 'relogio' : 'atendimento'} />
            </span>
            <span className="espera__texto">
              <strong className="espera__nome">{nomeDe(x.automacao_id)}</strong>
              <span className="espera__oque">{oQueAguarda(x, blocos)}</span>
              <span className="espera__desde">{haQuanto(x.desde, agora)}</span>
            </span>
            {podeCancelar &&
              (confirmando === x.id ? (
                <span className="espera__confirmar">
                  Cancelar? A automação para aqui para esta pessoa.
                  <Botao variante="fantasma" miudo onClick={() => definirConfirmando(null)}>
                    Não
                  </Botao>
                  <Botao
                    variante="perigo"
                    miudo
                    onClick={() => cancelar.mutate(x.id)}
                    desabilitado={cancelar.isPending}
                    motivo="Cancelando…"
                  >
                    Sim, cancelar
                  </Botao>
                </span>
              ) : (
                <MenuAcoes
                  rotulo={`Ações da espera de ${nomeDe(x.automacao_id)}`}
                  itens={[
                    {
                      rotulo: 'Cancelar a espera',
                      perigo: true,
                      aoClicar: () => definirConfirmando(x.id),
                    },
                  ]}
                />
              ))}
          </li>
        ))}
      </ul>

      <footer className="espera__rodape">
        <span>
          Exibindo {visiveis.length} de {total} {total === 1 ? 'execução' : 'execuções'}
        </span>
        {total > ESPERAS_A_MOSTRAR && (
          <button
            type="button"
            className="espera__ver-todas"
            aria-expanded={todas}
            onClick={() => definirTodas(!todas)}
          >
            {todas ? 'Ver menos' : 'Ver todas'}
            <Icone nome={todas ? 'abaixo' : 'direita'} />
          </button>
        )}
      </footer>
    </section>
  );
}

/**
 * A versão em que a execução rodou. Com o mapa à mão, ela abre aquela versão
 * (a de agora, se é a mesma); sem ele, é só a marca.
 */
function VersaoDaLinha({
  versao,
  antiga,
  aoAbrir,
}: {
  versao: number;
  antiga: boolean;
  aoAbrir?: () => void;
}): ReactNode {
  const texto = `v${versao}${antiga ? ' · anterior' : ''}`;
  if (!aoAbrir) {
    return (
      <span className="acompanhamento__versao" data-antiga={antiga ? 'sim' : undefined}>
        {texto}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="acompanhamento__versao"
      data-antiga={antiga ? 'sim' : undefined}
      aria-label={`${texto}: abrir o mapa da versão ${versao}`}
      onClick={(ev) => {
        // A linha abre o detalhe; a versão abre o mapa, e só ele.
        ev.stopPropagation();
        aoAbrir();
      }}
    >
      {texto}
    </button>
  );
}
